import {
  signal,
  computed,
  effect,
  WritableSignal,
  Injector,
} from '@angular/core';
import {
  CFTextReviewItem,
  CFTextReviewStatus,
} from '@ui/cf-text-review/cf-text-review.component';
import {
  ReviewInterviewDetails,
  ReviewPage,
  ReviewPageStatus,
  ReviewService,
} from './review.service';
import { ApplicantReviewDetails } from './review.service';
import { WorkAreaService } from '../../../work-area/work-area.service';
import { TimelineDetails, GapEntry } from './timeline';
import { environment } from '@environments/environment';

export interface ReviewState {
  loading: boolean;
  pages: ReviewPage[];
  reviewDetails?: ApplicantReviewDetails;
  reviewNotes: Map<string, string>;
  initialized: boolean;
}

export class ReviewAppStateService {
  private state: WritableSignal<ReviewState>;

  constructor(
    private jobId: number,
    private reviewService: ReviewService,
    private workAreaService: WorkAreaService,
    private injector: Injector
  ) {
    this.state = signal<ReviewState>({
      loading: true,
      pages: [],
      reviewDetails: undefined,
      reviewNotes: new Map<string, string>(),
      initialized: false,
    });

    const storageEffect = effect(
      () => {
        const currentState = this.state();
        if (currentState.reviewDetails) {
          sessionStorage.setItem(
            this.getCacheName(),
            JSON.stringify(currentState.reviewDetails)
          );
        }
      },
      { injector: this.injector }
    );
    // Add effect for auto-saving review notes to session storage
    const notesEffect = effect(
      () => {
        const notes = this.state().reviewNotes;
        if (notes.size > 0) {
          this.setReviewNotesInSession(notes);
        } else {
          sessionStorage.removeItem(this.getNotesCacheName());
        }
      },
      { injector: this.injector }
    );
  }

  async initialize(): Promise<void> {
    await this.loadReviewDetails();
  }

  private async loadReviewDetails() {
    try {
      const jobDetails = await this.workAreaService.getJobDetails(this.jobId);
      const details = await this.reviewService.fetchReviewDetails(
        this.jobId,
        jobDetails.applicantId,
        jobDetails.applicationId
      );

      if (details) {
        const cacheName = this.getCacheName();
        sessionStorage.setItem(cacheName, JSON.stringify(details));
        this.updateReviewDetails(details);
      }
    } finally {
      this.state.update((s) => ({ ...s, loading: false }));
    }
  }

  private updateReviewDetails(details: ApplicantReviewDetails) {
    // Convert reviewer notes to Map
    const reviewNotes = new Map<string, string>();
    details.reviewerNotes.forEach((note) => {
      reviewNotes.set(note.itm, note.txt);
    });

    // Merge with any existing session storage notes to preserve user changes
    const sessionNotes = this.getReviewNotesFromSession();
    if (sessionNotes) {
      sessionNotes.forEach((value, key) => {
        reviewNotes.set(key, value);
      });
    }

    // Update items with concerns
    const items = details.reviewItems;
    details.reviewerNotes.forEach((note) => {
      const item = items.find((i) => i.dataItem === note.itm);
      if (item && item.status !== CFTextReviewStatus.Feedback) {
        item.status = CFTextReviewStatus.Concern;
      }
    });

    details.reviewItems = items;

    this.state.update((s) => ({
      ...s,
      reviewDetails: details,
      reviewNotes,
      initialized: true,
    }));
  }

  // Derive all computed values from the job state signal
  readonly items = computed(() => {
    // Access jobState to create dependency
    const currentState = this.state();
    const notes = this.state().reviewNotes;

    if (!currentState.reviewDetails?.reviewItems?.length) return [];

    return currentState.reviewDetails?.reviewItems
      .filter((i) => i.status !== CFTextReviewStatus.None)
      .map((i) => {
        const note = notes?.get(i.dataItem);
        return note ? { ...i, reviewerNote: note } : i;
      });
  });

  readonly loading = computed(() => this.state().loading);
  readonly initialized = computed(() => this.state().initialized);
  readonly pages = computed(() => this.state().pages);
  readonly reviewNotes = computed(() => this.state().reviewNotes);
  readonly reviewItems = computed(
    () => this.state().reviewDetails?.reviewItems
  );
  readonly reviewPages = computed(
    () => this.state().reviewDetails?.reviewPages ?? []
  );

  warning = () => this.state().reviewDetails?.applicantWarning;

  readonly reviewDetails = () => this.state().reviewDetails;
  applicantProfileOrInitials = () => {
    if (this.state().reviewDetails?.details.profilePicture)
      return this.state().reviewDetails?.details.profilePicture;

    // No profile; use initials
    return `${this.state().reviewDetails?.details.firstName.slice(
      0,
      1
    )}${this.state().reviewDetails?.details.lastName.slice(0, 1)}`;
  };
  assignments = () => this.state().reviewDetails?.assignments ?? [];
  criminalSummary = () => this.state().reviewDetails?.criminalSummary;

  lastPage = () => this.state().reviewDetails?.lastPage;
  placedAtEvent = () =>
    this.state().reviewDetails?.details.placedAtEvent ?? false;
  interviewDetails = () => {
    const ard = this.state().reviewDetails!;
    const i = new ReviewInterviewDetails();
    i.interviewerId = ard.details.interviewerId;
    i.interviewDate = ard.details.interviewDate;
    i.interviewTime = ard.details.interviewTime;
    i.interviewType = ard.details.interviewType;
    i.interviewerName = `${ard.details.interviewerFirstName} ${ard.details.interviewerLastName}`;
    i.interviewCopied = ard.details.interviewCopied ?? false;
    if (ard.details.interviewerPicture) {
      i.interviewerProfile = ard.details.interviewerPicture;
    } else {
      i.interviewerProfile = `${ard.details.interviewerFirstName?.slice(
        0,
        1
      )}${ard.details.interviewerLastName?.slice(0, 1)}`;
    }
    return i;
  };
  applicationCountry = () =>
    this.state().reviewDetails?.details.applicationCountry;
  ageOnJuneFirst = () => this.state().reviewDetails?.details.ageOnJuneFirst;
  applicationType = () =>
    this.state().reviewDetails?.application.applicationType;
  fulltimeStudent = () => this.state().reviewDetails?.details.fulltimeStudent;
  embassyPosition = () => this.state().reviewDetails?.details.embassyPosition;
  skills = () => this.state().reviewDetails?.skills;
  addOrUpdateExperienceReviewItem = async (
    item: CFTextReviewItem,
    tld: TimelineDetails,
    entry: GapEntry
  ) => {
    // Call the method from the returned object instead of state
    const reviewState = this.state();
    this.addOrUpdateReviewItem(item);

    if (entry) {
      const currentState = this.state();
      if (!currentState.reviewDetails) {
        throw new Error(`No review detail specified in storage`);
      }

      tld.gapDisplay.forEach((gd) => {
        if (gd.name === item.dataItem) {
          gd = entry;
        }
      });
      currentState.reviewDetails.experience!.timeline = tld;
      this.state.update((s) => ({
        ...s,
        reviewDetails: currentState.reviewDetails,
      }));
    }
  };

  setPageReviewed = (index: number) => {
    const ard = this.state().reviewDetails!;
    const pages = ard?.reviewPages;

    pages[index].viewed = true;
    pages[index].status = this.updatePageStatus(ard, pages[index]);
    ard.reviewPages = pages.slice();

    this.state.update((s) => ({
      ...s,
      reviewDetails: ard,
    }));
  };

  setReviewNote = (dataItem: string, note: string) => {
    const currentNotes = this.state().reviewNotes;
    const newNotes = new Map(currentNotes);
    newNotes.set(dataItem, note);
    
    // Trigger state update to persist changes and update UI
    this.state.update((s) => ({
      ...s,
      reviewNotes: newNotes,
    }));
  };
  updateReviewItemValidity = (dataItem: string, isValid: boolean) => {
    const reviewDetails = this.state().reviewDetails;
    const items = reviewDetails?.reviewItems;
    if (!items) {
      return;
    }
    const itemIndex = items.findIndex((i) => i.dataItem === dataItem);
    if (itemIndex > -1) {
      items[itemIndex].isValid = isValid;
      reviewDetails.reviewItems = items.slice();

      this.state.update((s) => ({
        ...s,
        reviewDetails: reviewDetails,
      }));
    }
  };

  setLastPage = (page: number) => {
    this.state().reviewDetails!.lastPage = page;
  };

  fullName = () => {
    const ard = this.state().reviewDetails;
    const m = ard?.details.middleName ? `${ard.details.middleName} ` : '';
    return `${ard?.details.firstName} ${m}${ard?.details.lastName}`;
  };
  applicantId = () => this.state().reviewDetails?.details.applicantId;
  applicationId = () => this.state().reviewDetails?.details.applicationId;
  timelineDetails = () => this.state().reviewDetails?.experience?.timeline;
  addOrUpdateReviewItem = (item: CFTextReviewItem) => {
    let ard = this.state().reviewDetails;
    if (!ard) {
      throw new Error(`No review detail specified in storage`);
    }
    let items = ard.reviewItems;
    // let save = false;
    // let remove = false;

    // If the item is now listed as NOT having a status,
    // and it is currently present, remove it.
    const e = items.findIndex((ri) => ri.dataItem === item.dataItem);
    if (e > -1) {
      if (item.status === CFTextReviewStatus.None) {
        // Must remove
        //items = items.filter((i, idx) => idx !== e).slice();
        items[e] = item;

        // Remove any attached notes
        const notes = this.state().reviewNotes;
        if (notes.has(item.dataItem)) {
          notes.delete(item.dataItem);
          this.state.update((s) => ({
            ...s,
            reviewNotes: notes,
          }));
        }
      } else {
        // Update item
        items[e].status = item.status;
        // save = true;
      }
    } else {
      // Add new item
      items.push(item);
      // save = true;
    }

    ard.reviewItems = items.slice();
    
    //update page status to keep the overall indicator up to date
    const pageToUpdate = ard.reviewPages.find(p => p.route === item.category);
    if (pageToUpdate && pageToUpdate.viewed) {
      pageToUpdate.status = this.updatePageStatus(ard, pageToUpdate);
    }
    
    this.state.update((s) => ({
      ...s,
      reviewDetails: ard,
    }));
  };

  private clearSession(): void {
    const cacheName = this.getCacheName();
    sessionStorage.removeItem(cacheName);
    const notesCacheName = this.getNotesCacheName();
    sessionStorage.removeItem(notesCacheName);
  }

  private getNotesCacheName(): string {
    return `${ReviewService.SessionReviewNotes}-${this.jobId}`;
  }

  private getCacheName(): string {
    return `${ReviewService.SessionApplicantToReview}-${this.jobId}`;
  }

  private updatePageStatus(
    ard: ApplicantReviewDetails,
    page: ReviewPage
  ): ReviewPageStatus {
    const items = ard.reviewItems.filter((ri) => ri.category == page.route);
    const issueItems = items.filter(
      (fi) => fi.status === CFTextReviewStatus.Concern
    );
    if (issueItems.length > 0) return ReviewPageStatus.Issue;

    const noteItems = items.filter(
      (fi) => fi.status === CFTextReviewStatus.Feedback
    );
    if (noteItems.length > 0) return ReviewPageStatus.Note;

    return ReviewPageStatus.Good;
  }

  private setReviewNotesInSession(notes: Map<string, string>): void {
    const notesCacheName = this.getNotesCacheName();
    sessionStorage.setItem(notesCacheName, JSON.stringify([...notes]));
  }

  private getReviewNotesFromSession(): Map<string, string> | null {
    const notesCacheName = this.getNotesCacheName();
    const stored = sessionStorage.getItem(notesCacheName);
    if (stored) {
      try {
        const notesArray = JSON.parse(stored) as [string, string][];
        return new Map(notesArray);
      } catch (e) {
        console.error('Failed to parse review notes from session storage:', e);
        return null;
      }
    }
    return null;
  }

  clearState() {
    this.clearSession();
  }
}
